/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useParams, Link as Linkreact } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Box,
  LinearProgress,
  Stack,
  Alert,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useQuery, useMutation } from '@apollo/client'

import removeParticipantsMutation from '../../graphql/mutation/removeParticipants'
import Breadcrumb from '../../components/BreadcrumbLayout'
import createOrUpdateParticipantMutation from '../../graphql/mutation/createOrUpdateParticipant'
import eventByIdQuery from '../../graphql/query/eventById'
import activitiesCountQuery from '../../graphql/query/activitiesCount'
import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { useParticipants } from '../../hooks'
import {
  ParticipantDetailsDialog,
  ParticipantFilterModal,
  ParticipantTableSection,
  AddParticipantModal,
  ProfileModal,
} from '../../components/participant'

// Header aliases that map onto top-level participant fields. Every other
// column in the sheet falls through to `options` instead of being dropped —
// the mobile app renders race/message/note/year out of there.
const TOP_LEVEL_ALIASES = {
  firstname: ['First Name', 'firstName', 'firstname', 'first_name'],
  lastname: ['Last Name', 'lastName', 'lastname', 'last_name'],
  bib: ['Bib', 'bib'],
  row: ['Row', 'row'],
  gender: ['Gender', 'gender'],
  shirtType: ['Shirt Type', 'shirtType', 'shirt_type'],
  shirtSize: ['Shirt Size', 'shirtSize', 'shirt_size'],
  status: ['Status', 'status'],
  birthDate: ['Birth Date', 'birthDate', 'birthdate', 'birth_date'],
  ageCategory: ['Age Category', 'ageCategory', 'age_category'],
  // Legacy sheets carry both: lowercase holds 'Thai', 'Nationality' holds 'ไทย'.
  nationality: ['nationality', 'Nationality'],
  nationalId: ['National ID', 'National Id', 'nationalId', 'national_id'],
  displayName: ['Display Name', 'displayName', 'display_name'],
  ticketCode: ['Ticket Code', 'ticketCode', 'ticket_code'],
  ticketType: ['Ticket Type', 'ticketType', 'ticket_type'],
  photo: ['Photo', 'photo'],
}
const TOP_LEVEL_HEADERS = new Set(Object.values(TOP_LEVEL_ALIASES).flat())

const ParticipantPage = () => {
  const { user } = useContext(AuthContext)
  const { showSuccess, showError, showInfo, showWarning } = useToast()
  const params = useParams()
  const [bibFilter, setBibFilter] = useState('')
  const [rowFilter, setRowFilter] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [genderFilter, setGenderFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All')
  const [json, setJson] = useState('')
  const [open, setOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [checkIn, setCheckIn] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [faceSearchPreview, setFaceSearchPreview] = useState(null)
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const [genderFilterModal, setGenderFilterModal] = useState({
    All: true,
    male: false,
    female: false,
  })
  const [shirtTypeFilterModal, setShirtTypeFilterModal] = useState({
    All: true,
    'Short Sleeves': false,
    Vest: false,
    Unisex: false,
  })
  const [shirtSizeFilterModal, setShirtSizeFilterModal] = useState({
    All: true,
    Xs: false,
    S: false,
    M: false,
    L: false,
    XL: false,
    '2XL': false,
    '3XL': false,
  })
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [activeShirtTypeFilter, setActiveShirtTypeFilter] = useState(null)
  const [activeShirtSizeFilter, setActiveShirtSizeFilter] = useState(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [openAddParticipantModal, setOpenAddParticipantModal] = useState(false)
  const [openProfileModal, setOpenProfileModal] = useState(false)
  const [selectedProfileParticipant, setSelectedProfileParticipant] = useState(null)

  // Fetches the full participant list in batches of 5000. The `limit` arg of
  // the `participants` query defaults to 1000, so an unpaginated query silently
  // truncated large events.
  const {
    loading,
    participants,
    refetch,
    error: participantsError,
  } = useParticipants(params.eventId)
  const event = useQuery(eventByIdQuery, {
    variables: {
      _id: params.eventId,
    },
  })
  const activitiesCount = useQuery(activitiesCountQuery, {
    variables: {
      eventId: params.eventId,
    },
  })
  const [createOrUpdateParticipant] = useMutation(createOrUpdateParticipantMutation)
  const [removeParticipants] = useMutation(removeParticipantsMutation)

  useEffect(() => {
    if (activitiesCount.data) {
      const totalCheckin = activitiesCount.data.activitiesCount
        .filter(({ lastActivity }) => !!lastActivity)
        .reduce((prev, { count }) => prev + count, 0)
      setCheckIn(totalCheckin)
    }
  }, [activitiesCount])

  const selectFile = async (e) => {
    e.preventDefault()
    e.currentTarget.files[0].arrayBuffer().then((arrayBuffer) => {
      const wb = XLSX.read(arrayBuffer, { type: 'buffer' })
      const wsname = wb.SheetNames[0]
      const ws = wb.Sheets[wsname]
      const jsonObj = XLSX.utils.sheet_to_json(ws)
      setJson(jsonObj)
      setOpen(true)
    })
  }

  // Normalize Excel row to backend participant shape (supports "First Name", "Bib", "Shirt Type", etc.)
  const mapExcelRecordToParticipant = (record) => {
    const get = (...keys) => {
      for (const k of keys) {
        const v = record[k]
        if (v !== undefined && v !== null && v !== '') return v
      }
      return ''
    }
    const firstname = get(...TOP_LEVEL_ALIASES.firstname) || ''
    const lastname = get(...TOP_LEVEL_ALIASES.lastname) || ''
    const bib = get(...TOP_LEVEL_ALIASES.bib)
    const row = get(...TOP_LEVEL_ALIASES.row)
    const gender = get(...TOP_LEVEL_ALIASES.gender)
    const shirtType = get(...TOP_LEVEL_ALIASES.shirtType) || ''
    const shirtSize = get(...TOP_LEVEL_ALIASES.shirtSize) || ''
    const status = get(...TOP_LEVEL_ALIASES.status)
    const birthDate = get(...TOP_LEVEL_ALIASES.birthDate)
    const ageCategory = get(...TOP_LEVEL_ALIASES.ageCategory)
    const nationality = get(...TOP_LEVEL_ALIASES.nationality)
    const nationalId = get(...TOP_LEVEL_ALIASES.nationalId)
    const displayName = get(...TOP_LEVEL_ALIASES.displayName) || `${firstname} ${lastname}`.trim()
    const ticketCode = get(...TOP_LEVEL_ALIASES.ticketCode)
    const ticketType = get(...TOP_LEVEL_ALIASES.ticketType)
    const photo = get(...TOP_LEVEL_ALIASES.photo)

    // Keep the leftover columns under their original header, the way the
    // pre-refactor importer did with its rest-spread.
    const options = Object.entries(record).reduce((acc, [key, value]) => {
      if (!TOP_LEVEL_HEADERS.has(key)) acc[key] = value
      return acc
    }, {})
    if (ticketCode) options.ticketCode = String(ticketCode)
    if (ticketType) options.ticketType = String(ticketType)
    if (status) options.status = String(status)
    // The mobile app reads the birth date out of `options`, not the top level.
    if (birthDate) options.birthDate = String(birthDate)

    return {
      eventId: params.eventId,
      bib: String(bib ?? ''),
      row: String(row ?? ''),
      firstname: String(firstname ?? ''),
      lastname: String(lastname ?? ''),
      displayName: String(displayName ?? ''),
      gender: String(gender ?? ''),
      nationalId: String(nationalId ?? ''),
      nationality: String(nationality ?? ''),
      ageCategory: String(ageCategory ?? ''),
      birthDate: birthDate ? String(birthDate) : undefined,
      photo: photo || undefined,
      shirt: { type: String(shirtType ?? ''), size: String(shirtSize ?? '') },
      // Always send `options`, even empty: the app crashes on a missing one.
      options,
    }
  }

  const handleImport = async () => {
    setOpen(false)
    setImporting(true)
    const recordCount = json.length
    const records = [...json]
    let successCount = 0
    let errorCount = 0

    try {
      while (records.length > 0) {
        const results = await Promise.allSettled(
          records.splice(0, 10).map((record) => {
            const mappedData = mapExcelRecordToParticipant(record)
            return createOrUpdateParticipant({
              variables: {
                filter: mappedData,
              },
            })
          })
        )

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            successCount++
          } else {
            errorCount++
            console.error('Import error:', result.reason)
          }
        })

        setProgress(((recordCount - records.length) * 100) / recordCount)
      }

      refetch({ force: true })
      event.refetch()
      setProgress(100)
      setImporting(false)

      if (errorCount > 0) {
        showWarning(
          `Imported ${successCount} participants. ${errorCount} failed.`,
          'Import Completed',
          8000
        )
      } else {
        showSuccess(`Successfully imported ${successCount} participants.`, 'Import Completed')
      }
    } catch (error) {
      console.error('Import error:', error)
      setImporting(false)
      showError('Failed to import participants. Please try again.')
    }
  }

  const handleClear = () => {
    setNameFilter('')
    setBibFilter('All')
    setRowFilter('All')
    setGenderFilter('All')
    setStatusFilter('All')
    setGenderFilterModal({ All: true, male: false, female: false })
    setShirtTypeFilterModal({
      All: true,
      'Short Sleeves': false,
      Vest: false,
      Unisex: false,
    })
    setShirtSizeFilterModal({
      All: true,
      Xs: false,
      S: false,
      M: false,
      L: false,
      XL: false,
      '2XL': false,
      '3XL': false,
    })
    setActiveShirtTypeFilter(null)
    setActiveShirtSizeFilter(null)
    setHasActiveFilters(false)
    // Filters are applied client-side, so clearing them needs no refetch.
  }

  // Remove individual filter
  const handleRemoveFilter = (filterType) => {
    switch (filterType) {
      case 'name':
        setNameFilter('')
        break
      case 'bib':
        setBibFilter('All')
        break
      case 'row':
        setRowFilter('All')
        break
      case 'gender':
        setGenderFilter('All')
        setGenderFilterModal({ All: true, male: false, female: false })
        break
      case 'status':
        setStatusFilter('All')
        break
      case 'shirtType':
        setActiveShirtTypeFilter(null)
        setShirtTypeFilterModal({
          All: true,
          'Short Sleeves': false,
          Vest: false,
          Unisex: false,
        })
        break
      case 'shirtSize':
        setActiveShirtSizeFilter(null)
        setShirtSizeFilterModal({
          All: true,
          Xs: false,
          S: false,
          M: false,
          L: false,
          XL: false,
          '2XL': false,
          '3XL': false,
        })
        break
      default:
        break
    }

    // Re-check if any filters are still active
    const hasFilters =
      (filterType !== 'name' && nameFilter !== '') ||
      (filterType !== 'bib' && bibFilter !== '' && bibFilter !== 'All') ||
      (filterType !== 'row' && rowFilter !== '' && rowFilter !== 'All') ||
      (filterType !== 'gender' && genderFilter !== '' && genderFilter !== 'All') ||
      (filterType !== 'status' && statusFilter !== '' && statusFilter !== 'All') ||
      (filterType !== 'shirtType' && activeShirtTypeFilter !== null) ||
      (filterType !== 'shirtSize' && activeShirtSizeFilter !== null)
    setHasActiveFilters(hasFilters)

    // Filters are applied client-side over the full list, so no refetch needed.
  }

  const handleFilterModalOpen = () => {
    // Sync filter modal state with current filter state when opening modal
    if (genderFilter !== '' && genderFilter !== 'All') {
      setGenderFilterModal({
        All: false,
        male: genderFilter.toLowerCase() === 'male',
        female: genderFilter.toLowerCase() === 'female',
      })
    } else {
      setGenderFilterModal({ All: true, male: false, female: false })
    }

    if (activeShirtTypeFilter !== null) {
      setShirtTypeFilterModal({
        All: false,
        'Short Sleeves': activeShirtTypeFilter === 'Short Sleeves',
        Vest: activeShirtTypeFilter === 'Vest',
        Unisex: activeShirtTypeFilter === 'Unisex',
      })
    } else {
      setShirtTypeFilterModal({
        All: true,
        'Short Sleeves': false,
        Vest: false,
        Unisex: false,
      })
    }

    if (activeShirtSizeFilter !== null) {
      setShirtSizeFilterModal({
        All: false,
        Xs: activeShirtSizeFilter === 'Xs',
        S: activeShirtSizeFilter === 'S',
        M: activeShirtSizeFilter === 'M',
        L: activeShirtSizeFilter === 'L',
        XL: activeShirtSizeFilter === 'XL',
        '2XL': activeShirtSizeFilter === '2XL',
        '3XL': activeShirtSizeFilter === '3XL',
      })
    } else {
      setShirtSizeFilterModal({
        All: true,
        Xs: false,
        S: false,
        M: false,
        L: false,
        XL: false,
        '2XL': false,
        '3XL': false,
      })
    }

    setOpenFilterModal(true)
  }

  const handleFilterModalClose = () => {
    setOpenFilterModal(false)
  }

  const handleFilterModalApply = () => {
    // Apply filters from modal
    const selectedGenders = Object.keys(genderFilterModal).filter(
      (key) => genderFilterModal[key] && key !== 'All'
    )
    const selectedShirtTypes = Object.keys(shirtTypeFilterModal).filter(
      (key) => shirtTypeFilterModal[key] && key !== 'All'
    )
    const selectedShirtSizes = Object.keys(shirtSizeFilterModal).filter(
      (key) => shirtSizeFilterModal[key] && key !== 'All'
    )

    // Update gender filter if selected (convert to lowercase to match data)
    if (selectedGenders.length > 0) {
      setGenderFilter(selectedGenders[0].toLowerCase())
    } else {
      setGenderFilter('All')
    }

    // Update shirt type and size filters
    if (selectedShirtTypes.length > 0) {
      setActiveShirtTypeFilter(selectedShirtTypes[0])
    } else {
      setActiveShirtTypeFilter(null)
    }

    if (selectedShirtSizes.length > 0) {
      setActiveShirtSizeFilter(selectedShirtSizes[0])
    } else {
      setActiveShirtSizeFilter(null)
    }

    // Name/bib/row/status filters are applied client-side in `sortedParticipants`

    // Check if any filters are active (use the updated values)
    const updatedGenderFilter = selectedGenders.length > 0 ? selectedGenders[0] : 'All'
    const updatedShirtTypeFilter = selectedShirtTypes.length > 0 ? selectedShirtTypes[0] : null
    const updatedShirtSizeFilter = selectedShirtSizes.length > 0 ? selectedShirtSizes[0] : null

    const hasFilters =
      nameFilter !== '' ||
      (bibFilter !== '' && bibFilter !== 'All') ||
      (rowFilter !== '' && rowFilter !== 'All') ||
      (updatedGenderFilter !== '' && updatedGenderFilter !== 'All') ||
      (statusFilter !== '' && statusFilter !== 'All') ||
      updatedShirtTypeFilter !== null ||
      updatedShirtSizeFilter !== null
    setHasActiveFilters(hasFilters)

    // Reset page to 1 when applying filters
    setPage(1)
    setOpenFilterModal(false)
  }

  const handleFilterCheckboxChange = (filterType, key) => {
    if (key === 'All') {
      // If "All" is checked, uncheck everything else
      if (filterType === 'gender') {
        setGenderFilterModal({ All: true, male: false, female: false })
      } else if (filterType === 'shirtType') {
        setShirtTypeFilterModal({
          All: true,
          'Short Sleeves': false,
          Vest: false,
          Unisex: false,
        })
      } else if (filterType === 'shirtSize') {
        setShirtSizeFilterModal({
          All: true,
          Xs: false,
          S: false,
          M: false,
          L: false,
          XL: false,
          '2XL': false,
          '3XL': false,
        })
      }
    } else {
      // If a specific option is checked, uncheck "All"
      if (filterType === 'gender') {
        setGenderFilterModal({
          ...genderFilterModal,
          [key]: !genderFilterModal[key],
          All: false,
        })
      } else if (filterType === 'shirtType') {
        setShirtTypeFilterModal({
          ...shirtTypeFilterModal,
          [key]: !shirtTypeFilterModal[key],
          All: false,
        })
      } else if (filterType === 'shirtSize') {
        setShirtSizeFilterModal({
          ...shirtSizeFilterModal,
          [key]: !shirtSizeFilterModal[key],
          All: false,
        })
      }
    }
  }

  const handleFaceSearchFile = (e) => {
    const file = e.target.files[0] || (e.dataTransfer && e.dataTransfer.files[0])
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setFaceSearchPreview(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    handleFaceSearchFile(e)
  }

  const handleExport = () => {
    if (sortedParticipants.length === 0) {
      showError('No data available to export.')
      return
    }

    try {
      const workbook = XLSX.utils.book_new()
      const worksheetData = sortedParticipants.map((participant) => ({
        'First Name': participant.firstname || '',
        'Last Name': participant.lastname || '',
        Gender: participant.gender || '',
        Bib: participant.bib || '',
        Row: participant.row || '',
        'Shirt Type': participant.shirt?.type || '-',
        'Shirt Size': participant.shirt?.size || '-',
        Status: participant.lastActivity?.status || '-',
      }))
      const worksheet = XLSX.utils.json_to_sheet(worksheetData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Participants')
      XLSX.writeFile(workbook, `participants-${params.eventId}-${Date.now()}.xlsx`)
      showSuccess(
        `Exported ${sortedParticipants.length} participant(s) successfully.`,
        'Export Successful'
      )
    } catch (error) {
      console.error('Export error:', error)
      showError('Failed to export participants. Please try again.')
    }
  }

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Sort and filter participants - MUST be before early returns
  const sortedParticipants = useMemo(() => {
    if (!participants) return []

    // Apply all filters client-side (the full list is fetched via useParticipants)
    let filtered = [...participants]

    // Apply name filter (matches display/first/last name, case-insensitive)
    if (nameFilter !== '') {
      const query = nameFilter.toLowerCase()
      filtered = filtered.filter((participant) => {
        const haystacks = [
          participant.displayName,
          participant.firstname,
          participant.lastname,
          `${participant.firstname || ''} ${participant.lastname || ''}`.trim(),
        ].filter(Boolean)
        return haystacks.some((value) => value.toLowerCase().includes(query))
      })
    }

    // Apply bib filter
    if (bibFilter !== '' && bibFilter !== 'All') {
      filtered = filtered.filter((participant) => String(participant.bib ?? '') === String(bibFilter))
    }

    // Apply row filter (case-insensitive, matches the old r -> R normalization)
    if (rowFilter !== '' && rowFilter !== 'All') {
      const row = rowFilter.replace(/r/g, 'R')
      filtered = filtered.filter((participant) => String(participant.row ?? '').toUpperCase() === row.toUpperCase())
    }

    // Apply gender filter
    if (genderFilter !== '' && genderFilter !== 'All') {
      filtered = filtered.filter((participant) =>
        String(participant.gender ?? '').toLowerCase() === genderFilter.toLowerCase()
      )
    }

    // Apply status filter
    if (statusFilter !== '' && statusFilter !== 'All') {
      filtered = filtered.filter((participant) => {
        const participantStatus = participant.lastActivity?.status || '-'
        return participantStatus.toLowerCase() === statusFilter.toLowerCase()
      })
    }

    // Apply shirt size filter
    if (activeShirtSizeFilter !== null) {
      filtered = filtered.filter((participant) => {
        const participantShirtSize = participant.shirt?.size || ''
        return participantShirtSize === activeShirtSizeFilter
      })
    }

    // Apply shirt type filter
    if (activeShirtTypeFilter !== null) {
      filtered = filtered.filter((participant) => {
        const participantShirtType = participant.shirt?.type || ''
        return participantShirtType === activeShirtTypeFilter
      })
    }

    // Apply sorting
    if (!sortField) return filtered

    return filtered.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      // Handle nested fields
      if (sortField === 'name') {
        aValue = `${a.firstname || ''} ${a.lastname || ''}`.trim()
        bValue = `${b.firstname || ''} ${b.lastname || ''}`.trim()
      } else if (sortField === 'status') {
        aValue = a.lastActivity?.status || '-'
        bValue = b.lastActivity?.status || '-'
      } else if (sortField === 'shirtType') {
        aValue = a.shirt?.type || '-'
        bValue = b.shirt?.type || '-'
      } else if (sortField === 'shirtSize') {
        aValue = a.shirt?.size || '-'
        bValue = b.shirt?.size || '-'
      }

      // Handle numeric values
      if (sortField === 'bib') {
        aValue = parseInt(aValue) || 0
        bValue = parseInt(bValue) || 0
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [
    participants,
    nameFilter,
    bibFilter,
    rowFilter,
    genderFilter,
    sortField,
    sortDirection,
    statusFilter,
    activeShirtSizeFilter,
    activeShirtTypeFilter,
  ])

  // Pagination - MUST be before early returns
  const totalParticipants = sortedParticipants?.length || 0
  const totalPages = Math.ceil(totalParticipants / rowsPerPage)
  const paginatedParticipants = sortedParticipants
    ? sortedParticipants.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : []

  // Track whether any filter is active (debounced to match search typing).
  // The filters themselves are applied client-side in `sortedParticipants`,
  // so no refetch is needed here.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const hasFilters =
        nameFilter !== '' ||
        (bibFilter !== '' && bibFilter !== 'All') ||
        (rowFilter !== '' && rowFilter !== 'All') ||
        (genderFilter !== '' && genderFilter !== 'All') ||
        (statusFilter !== '' && statusFilter !== 'All') ||
        activeShirtTypeFilter !== null ||
        activeShirtSizeFilter !== null
      setHasActiveFilters(hasFilters)
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [
    nameFilter,
    bibFilter,
    rowFilter,
    genderFilter,
    statusFilter,
    activeShirtTypeFilter,
    activeShirtSizeFilter,
  ])

  // Reset page when filters change or rowsPerPage changes - MUST be before early returns
  useEffect(() => {
    setPage(1)
  }, [
    nameFilter,
    bibFilter,
    rowFilter,
    statusFilter,
    genderFilter,
    activeShirtTypeFilter,
    activeShirtSizeFilter,
    sortField,
    sortDirection,
    rowsPerPage,
  ])

  // Early returns - MUST be after all hooks
  if (user.role === 'Staff') {
    return 'Cannot Access'
  }
  if (event.data === undefined) {
    return 'Loading'
  }
  if (event.data.eventId === null) {
    return "Event Doesn't Exist"
  }

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      // Select all participants from current filtered/sorted data
      setSelectedItems(sortedParticipants || [])
      setSelectAll(true)
    } else {
      setSelectedItems([])
      setSelectAll(false)
    }
  }

  const handleCheckboxChange = (item) => {
    if (selectedItems.some((selected) => selected._id === item._id)) {
      setSelectedItems(selectedItems.filter((selected) => selected._id !== item._id))
    } else {
      setSelectedItems([...selectedItems, item])
    }
    setSelectAll(false)
  }

  const handleClose = () => {
    setOpenDialog(false)
  }

  const handleDelete = async () => {
    try {
      await removeParticipants({
        variables: {
          _id: selectedItems.map((r) => r._id),
          selectAll: selectAll,
          eventId: params.eventId,
        },
      })
      const deletedCount = selectAll ? 'all selected' : selectedItems.length
      refetch({ force: true })
      event.refetch()
      setSelectAll(false)
      setSelectedItems([])
      setOpenDialog(false)
      showSuccess(`Successfully deleted ${deletedCount} participant(s).`, 'Delete Successful')
    } catch (error) {
      console.error('Error deleting items:', error)
      showError('Failed to delete participants. Please try again.')
      setOpenDialog(false)
    }
  }

  // Calculate check-in percentage
  const checkInPercentage =
    event.data?.eventId?.participantCount > 0
      ? (checkIn / event.data.eventId.participantCount) * 100
      : 0

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
      }}>
      <Breadcrumb path="/events" titleLink="Event" titleTypo={event.data?.eventId?.name || ''} />

      {participantsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load participants: {participantsError.message}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: { xs: 1, sm: 1.25, md: 1.5 },
            mt: { xs: 1, sm: 1.25, md: 1.5 },
            color: 'text.primary',
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            lineHeight: 1.3,
          }}>
          Participants
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            lineHeight: 1.5,
            mb: { xs: 2.5, sm: 3, md: 3.5 },
            pb: { xs: 2.5, sm: 3, md: 3.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          Check the total number of participants
        </Typography>

        {/* Check-in Status Bar */}
        <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: { xs: 0.75, sm: 1, md: 1.25 },
            }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              }}>
              Checked In: {checkIn} / {event.data?.eventId?.participantCount || 0}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={checkInPercentage}
            sx={{
              height: { xs: 6, sm: 8, md: 10 },
              borderRadius: { xs: 3, sm: 4, md: 5 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.08)'
                  : 'rgba(255, 255, 255, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: { xs: 3, sm: 4, md: 5 },
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 1.5, md: 2 }}
          sx={{
            mb: { xs: 2.5, sm: 3, md: 3.5 },
            justifyContent: 'flex-end',
          }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenAddParticipantModal(true)}
            sx={{
              borderRadius: 1,
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1, sm: 1.125, md: 1.25 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
            }}>
            Add Participants
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<UploadFileIcon />}
            component="label"
            sx={{
              borderRadius: 1,
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1, sm: 1.125, md: 1.25 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
            }}>
            Import Participants
            <input onChange={selectFile} accept=".xlsx" style={{ display: 'none' }} type="file" />
          </Button>
        </Stack>
      </Box>

      {/* Face Search Section */}
      <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: { xs: 1.25, sm: 1.5, md: 2 },
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            lineHeight: 1.3,
          }}>
          Face search
        </Typography>
        <Paper
          elevation={0}
          sx={{
            border: (theme) =>
              `2px dashed ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
            borderRadius: 1,
            p: { xs: 2, sm: 2.5, md: 3 },
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.03)',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.05)',
            },
            transition: (theme) =>
              theme.transitions.create(['border-color', 'background-color'], {
                duration: theme.transitions.duration.short,
              }),
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('face-search-input').click()}>
          <input
            id="face-search-input"
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFaceSearchFile}
          />
          {faceSearchPreview ? (
            <Box>
              <Box
                component="img"
                src={faceSearchPreview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: 150, sm: 200, md: 250 },
                  borderRadius: 1,
                  mb: { xs: 1, sm: 1.25, md: 1.5 },
                }}
              />
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                }}>
                Click to change or drag and drop a new file
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  color: 'primary.main',
                  mb: { xs: 1, sm: 1.25, md: 1.5 },
                  opacity: 0.8,
                }}
              />
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  mb: { xs: 0.5, sm: 0.75, md: 1 },
                }}>
                Click to upload or <strong>drag and drop</strong>
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                }}>
                Upload an image to find participant information.
              </Typography>
            </>
          )}
        </Paper>
      </Box>

      {/* Table Section */}
      <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: { xs: 1.25, sm: 1.5, md: 2 },
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            lineHeight: 1.3,
          }}>
          Table: Shirt Type: Short Sleeves
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            mb: { xs: 1.25, sm: 1.5, md: 2 },
          }}>
          Short Sleeves
        </Typography>

        <ParticipantTableSection
          // Filter states
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          bibFilter={bibFilter}
          rowFilter={rowFilter}
          genderFilter={genderFilter}
          activeShirtTypeFilter={activeShirtTypeFilter}
          activeShirtSizeFilter={activeShirtSizeFilter}
          // Handlers
          onFilterModalOpen={handleFilterModalOpen}
          onExport={handleExport}
          onRemoveFilter={handleRemoveFilter}
          onClear={handleClear}
          onSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
          // Selection
          selectedItems={selectedItems}
          selectAll={selectAll}
          totalParticipants={totalParticipants}
          sortedParticipants={sortedParticipants}
          onCheckboxChange={handleCheckboxChange}
          onSelectAllChange={handleSelectAllChange}
          onDeleteClick={() => setOpenDialog(true)}
          onClearSelection={() => {
            setSelectedItems([])
            setSelectAll(false)
          }}
          // Table data
          loading={loading}
          participants={participants}
          paginatedParticipants={paginatedParticipants}
          hasActiveFilters={hasActiveFilters}
          // Pagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
          // Details dialog
          onDetailsClick={(row) => {
            setSelectedParticipant(row)
            setOpenDetailsDialog(true)
          }}
          onDeleteParticipantClick={(row) => {
            setSelectedItems([row])
            setOpenDialog(true)
          }}
          onProfileClick={(row) => {
            setSelectedProfileParticipant(row)
            setOpenProfileModal(true)
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the selected items?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDelete} color="primary">
            Confirm
          </Button>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{importing ? 'Import?' : 'Importing'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`This file has ${json.length} row(s), are you sure you want to import?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            No
          </Button>
          <Button onClick={() => handleImport()} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Importing Progress Dialog */}
      <Dialog
        open={importing}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Importing</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {progress === 100 ? 'Finished' : 'Importing'}
          </DialogContentText>
          {progress < 100 && <CircularProgress variant="indeterminate" value={progress} />}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImporting(false)}
            color="primary"
            autoFocus
            disabled={!(progress === 100)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      <ParticipantFilterModal
        open={openFilterModal}
        onClose={handleFilterModalClose}
        onApply={handleFilterModalApply}
        genderFilterModal={genderFilterModal}
        shirtTypeFilterModal={shirtTypeFilterModal}
        shirtSizeFilterModal={shirtSizeFilterModal}
        onFilterCheckboxChange={handleFilterCheckboxChange}
      />

      {/* Participant Details Dialog */}
      <ParticipantDetailsDialog
        open={openDetailsDialog}
        onClose={() => {
          setOpenDetailsDialog(false)
          setSelectedParticipant(null)
        }}
        participant={selectedParticipant}
        eventId={params.eventId}
        onUpdate={async () => {
          const { data: newData } = await refetch({ force: true })
          if (newData?.participants && selectedParticipant?._id) {
            const updatedParticipant = newData.participants.find((p) => p._id === selectedParticipant._id)
            if (updatedParticipant) {
              setSelectedParticipant(updatedParticipant)
            }
          }
        }}
      />

      {/* Add Participant Modal */}
      <AddParticipantModal
        open={openAddParticipantModal}
        onClose={() => setOpenAddParticipantModal(false)}
        eventId={params.eventId}
        onSuccess={() => {
          refetch({ force: true })
          event.refetch()
          // Toast notification is handled in AddParticipantModal.js
        }}
      />

      {/* Profile Modal */}
      <ProfileModal
        open={openProfileModal}
        onClose={() => {
          setOpenProfileModal(false)
          setSelectedProfileParticipant(null)
        }}
        participant={selectedProfileParticipant}
      />
    </Box>
  )
}

export default ParticipantPage
